export type PresetsDropdownPlacement = 'top' | 'bottom';

type TriggerRect = Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>;

type BuildPresetsDropdownPositionParams = {
    triggerRect: TriggerRect;
    dropdownHeight: number;
    viewportHeight: number;
    offset?: number;
    viewportPadding?: number;
};

export type PresetsDropdownPosition = {
    top: number;
    left: number;
    width: number;
    placement: PresetsDropdownPlacement;
};

const DEFAULT_OFFSET = 4;
const DEFAULT_VIEWPORT_PADDING = 4;
const MIN_DIMENSION = 1;

const PRESET_OPTION_HEIGHT = 24;
const PRESET_VERTICAL_PADDING = 2;
const MIN_PRESET_OPTIONS_COUNT = 1;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalizePositiveNumber = (value: number, fallback: number) =>
    Number.isFinite(value) && value > 0 ? value : fallback;

export const estimatePresetsDropdownHeight = (optionsCount: number) => {
    const normalizedOptionsCount = Math.max(
        MIN_PRESET_OPTIONS_COUNT,
        Math.trunc(normalizePositiveNumber(optionsCount, MIN_PRESET_OPTIONS_COUNT)),
    );

    return normalizedOptionsCount * PRESET_OPTION_HEIGHT + PRESET_VERTICAL_PADDING * 2;
};

export const buildPresetsDropdownPosition = ({
    triggerRect,
    dropdownHeight,
    viewportHeight,
    offset = DEFAULT_OFFSET,
    viewportPadding = DEFAULT_VIEWPORT_PADDING,
}: BuildPresetsDropdownPositionParams): PresetsDropdownPosition => {
    const normalizedDropdownHeight = Math.max(MIN_DIMENSION, Math.trunc(normalizePositiveNumber(dropdownHeight, 0)));
    const normalizedViewportHeight = Math.max(
        MIN_DIMENSION,
        Math.trunc(normalizePositiveNumber(viewportHeight, MIN_DIMENSION)),
    );
    const normalizedOffset = Math.max(0, Math.trunc(offset));
    const normalizedViewportPadding = Math.max(0, Math.trunc(viewportPadding));

    const spaceBelow = normalizedViewportHeight - triggerRect.bottom - normalizedViewportPadding;
    const spaceAbove = triggerRect.top - normalizedViewportPadding;
    const placement: PresetsDropdownPlacement =
        spaceBelow >= normalizedDropdownHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    const preferredTop =
        placement === 'bottom'
            ? triggerRect.bottom + normalizedOffset
            : triggerRect.top - normalizedDropdownHeight - normalizedOffset;

    const minTop = normalizedViewportPadding;
    const maxTop = Math.max(minTop, normalizedViewportHeight - normalizedDropdownHeight - normalizedViewportPadding);

    return {
        top: clamp(preferredTop, minTop, maxTop),
        left: triggerRect.left,
        width: triggerRect.width,
        placement,
    };
};
